import logo from './logo.svg';
import './App.css';
import {useEffect, useState} from "react";

import {FiSend} from "react-icons/fi";
import {FaHourglassHalf} from "react-icons/fa";

import {motion} from "framer-motion";

import * as MP4Box from "mp4box";
import {DataStream} from "mp4box";
import {extractFramesFromMP4} from "./VideoUtils";
import * as VideoUtils from "./VideoUtils";

function ThinkingIndicator() {
    return (
        <motion.span
            className="h-3 w-3 rounded-full thinking-indicator"
            animate={{
                scale: [1, 1.4, 1],      // 살짝 커졌다 돌아오고
                opacity: [1, 0.4, 1],    // 동시에 투명도 변화
            }}
            transition={{
                duration: 1.2,           // 한 사이클 1.2초
                repeat: Infinity,        // 무한 반복
                ease: "easeInOut",
            }}
        />
    );
}

function render() {
    const src = document.getElementById("video");
    const mask = document.getElementById("mask");
    const cvs = document.getElementById("canvas");
    const ctx = cvs.getContext("2d");

    const maskCvs = new OffscreenCanvas(1, 1);            // 오프스크린으로 처리
    const mCtx = maskCvs.getContext('2d', {willReadFrequently: true});

    if (src.readyState >= 2 && mask.readyState >= 2) {
        const w = cvs.width = src.videoWidth;
        const h = cvs.height = src.videoHeight;

        // 1) 원본 프레임
        ctx.drawImage(src, 0, 0, w, h);

        // 2) ===== 마스크: 그레이 → 알파 변환 =====
        maskCvs.width = w;
        maskCvs.height = h;
        mCtx.drawImage(mask, 0, 0, w, h);

        const frame = mCtx.getImageData(0, 0, w, h);
        const d = frame.data;              // Uint8ClampedArray

        // R(또는 (R+G+B)/3) → A 복사
        for (let i = 0; i < d.length; i += 4) {
            const lum = d[i];                // d[i] = R; 그레이라면 R=G=B
            d[i + 3] = lum;                 // alpha 채널
            d[i] = d[i + 1] = d[i + 2] = 255;    // 흰색(색상은 중요 X)
        }
        mCtx.putImageData(frame, 0, 0);

        // 3) 합성
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(maskCvs, 0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';

        // console.log("Rendered frame with mask applied");
    }
    requestAnimationFrame(render);
}

async function fetchMessage(logs) {
    const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({logs})
    });

    if (!response.ok) {
        throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data.message;
}

function App() {
    const [logs, setLogs] = useState([]);
    const [isActive, setIsActive] = useState(true);
    const [inputText, setInputText] = useState("");

    const personName = "Bot";

    useEffect(() => {
        // scroll to the bottom of the chat logs whenever logs change
        const logsContainer = document.querySelector(".logs");
        if (logsContainer) {
            logsContainer.scrollTop = logsContainer.scrollHeight;
        }
    }, [logs]);

    useEffect(() => {
        (async () => {
            const canvas = document.getElementById("canvas");
            const ctx = canvas.getContext("2d");

            const maskCvs = new OffscreenCanvas(1, 1);            // 오프스크린으로 처리
            const mCtx = maskCvs.getContext('2d', {willReadFrequently: true});

            while (true) {
                const queueColor = await VideoUtils.extractFramesFromMP4("/data/idle_001.mp4");
                const queueMask = await VideoUtils.extractFramesFromMP4("/data/idle_001_mask.mp4");

                while (true) {
                    const frameColor = await queueColor.dequeue();
                    const frameMask = await queueMask.dequeue();

                    if (!frameColor || !frameMask) {
                        console.warn("No more frames to process.");
                        break;  // No more frames to process
                    }

                    const w = canvas.width = 1000;
                    const h = canvas.height = 1000;

                    // const imageBitmap = await createImageBitmap(frame);
                    ctx.drawImage(frameColor, 0, 0, canvas.width, canvas.height);

                    maskCvs.width = w;
                    maskCvs.height = h;
                    mCtx.drawImage(frameMask, 0, 0, w, h);

                    const maskFrame = mCtx.getImageData(0, 0, w, h);
                    const maskData = maskFrame.data;              // Uint8ClampedArray

                    // R(또는 (R+G+B)/3) → A 복사
                    for (let i = 0; i < maskData.length; i += 4) {
                        const lum = maskData[i];                // maskData[i] = R; 그레이라면 R=G=B
                        maskData[i + 3] = lum;                 // alpha 채널
                        maskData[i] = maskData[i + 1] = maskData[i + 2] = 255;    // 흰색(색상은 중요 X)
                    }

                    mCtx.putImageData(maskFrame, 0, 0);

                    // 합성
                    ctx.globalCompositeOperation = 'destination-in';
                    ctx.drawImage(maskCvs, 0, 0, w, h);
                    ctx.globalCompositeOperation = 'source-over';

                    // release the imageBitmap to free up memory
                    // imageBitmap.close();
                    frameColor.close();
                    frameMask.close();

                    // 25fps
                    await new Promise(resolve => setTimeout(resolve, 40)); // 1000ms / 25fps = 40ms per frame

                }
            }
        })();
    }, []);

    async function submitMessage() {
        if (inputText.trim() === "") return;
        if (!isActive) return;

        setIsActive(false);
        setInputText("");
        setLogs((prevLogs) => [...prevLogs, inputText]);

        try {
            const response = await fetchMessage([...logs, inputText]);
            setLogs((prevLogs) => [...prevLogs, response]);
        } catch (error) {
            console.error("Error fetching message:", error);
            setLogs((prevLogs) => [...prevLogs, `Error fetching response: ${error.message}`]);
        }

        setIsActive(true);
    }

    return (
        <div className="wrapper">
            <div className="screen">
                <img id="body-image" src="/data/body_image.png" alt="Body"/>
                <canvas id="canvas"/>
                <div className={"chat"}>
                    <div className={"logs"}>
                        {
                            logs.map((log, index) => {
                                if (index % 2 === 0) {
                                    return <div key={index} className={"msg log-user"}>{log}</div>;
                                } else {
                                    return (
                                        <div key={index} className={"log-bot-container"}>
                                            <span className={"bot-name"}>{personName}</span>
                                            <div className={"msg log-bot"}>
                                                {log}
                                            </div>
                                        </div>
                                    );
                                }
                            })
                        }
                        {
                            !isActive && (
                                <div className={"log-bot-container"}>
                                    <span className={"bot-name"}>{personName} <ThinkingIndicator/></span>
                                    <div className={"msg log-bot log-bot-thinking"}>
                                        Thinking...
                                    </div>
                                </div>
                            )
                        }
                    </div>
                    <div className={"input-area"}>
                        <input id={"input-text"} type="text" placeholder="Type your message here..." value={inputText}
                               onChange={(e) => setInputText(e.target.value)}
                               onKeyDown={(e) => {
                                   if (e.key === 'Enter' && isActive) {
                                       submitMessage();
                                   }
                               }}
                        />
                        <button id={"input-button"} onClick={submitMessage}>
                            {isActive ? <FiSend size={18}/> : <FaHourglassHalf size={18}/>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
